/** PrimeVue SelectItem shape: {name: string, code: string} */
type SelectItem = { name: string; code: string }
import {
  LISTING_TYPE_OPTIONS,
  PROCESSING_STATUS_OPTIONS,
  TRANSACTION_TYPE_OPTIONS,
  REAL_ESTATE_CATEGORY_OPTIONS,
  POSTER_TYPE,
  DIRECTION_OPTIONS,
  PRICE_TYPE_OPTIONS,
  LEGAL_STATUS_OPTIONS,
  LAND_TYPE_OPTIONS,
  HANDOVER_STATUS_OPTIONS,
  APARTMENT_TYPE_OPTIONS,
  FURNITURE_STATUS_OPTIONS,
} from '@/modules/realestate/constants/realestate.const'
import { nl2br } from '@/lib/utils'

type Select = {
  label: string
  value: string
}

/** Helper: find matching SelectItem from options array */
const findSelectItem = (
  options: Select[],
  value: string | null | undefined,
): SelectItem | undefined => {
  if (!value) return undefined
  return options
    .map((item) => ({ name: item.label, code: item.value }))
    .find((item) => item.code === value)
}

export const initialValues = {
  // Basic info
  code: '',
  listing_type: {
    name: LISTING_TYPE_OPTIONS[0].label,
    code: LISTING_TYPE_OPTIONS[0].value,
  } as SelectItem,
  poster_id: '',
  processing_status: {
    name: PROCESSING_STATUS_OPTIONS[0].label,
    code: PROCESSING_STATUS_OPTIONS[0].value,
  } as SelectItem,
  poster_name: '',

  // Transaction & category
  transaction_type: {
    name: TRANSACTION_TYPE_OPTIONS[0].label,
    code: TRANSACTION_TYPE_OPTIONS[0].value,
  } as SelectItem,
  real_estate_category: { name: '', code: '' } as SelectItem,

  // Legal & land info
  legal_status: { name: '', code: '' } as SelectItem,
  map_sheet_number: '',
  plot_number: '',

  // Dimensions
  area: '',
  usage_area: '',
  width: '',
  length: '',

  // Price
  price_type: { name: '', code: '' } as SelectItem,
  price: '',
  price_deposit: '',

  // Direction
  direction: { name: '', code: '' } as SelectItem,
  main_door_direction: { name: '', code: '' } as SelectItem,
  balcony_direction: { name: '', code: '' } as SelectItem,

  // Category-specific fields
  property_type: { name: '', code: '' } as SelectItem,
  furniture_status: { name: '', code: '' } as SelectItem,
  handover_status: { name: '', code: '' } as SelectItem,
  land_type: { name: '', code: '' } as SelectItem,
  rooms: null as number | null,
  bath_rooms: null as number | null,
  floors: null as number | null,

  // Apartment/Office nested fields
  block_number: '',
  floor_number: '',

  // Apartment-specific fields
  unit_number: '',

  // Land-specific fields
  expansion_size: null as number | null,
  residential_area: null as number | null,

  // Building-specific fields
  estimated_cashflow: null as number | null,
  elevator_count: null as number | null,

  // Content
  attributes: [] as any,
  title: '',
  description: '',

  // Media
  photos: [] as any[],
  video: {},
  panorama: {},
}

export const mappingValuesToForm = (listing: any) => {
  const re = listing?.real_estate || {}

  return {
    code: listing.code,//String(listing?.id),
    listing_type: findSelectItem(LISTING_TYPE_OPTIONS, listing?.listing_type),
    poster_id: listing.poster?.id || '',
    processing_status: findSelectItem(PROCESSING_STATUS_OPTIONS, listing?.processing_status),
    poster_name: listing.poster?.full_name || '',
    poster_type: listing?.poster_type || POSTER_TYPE.COMPANY,

    transaction_type: findSelectItem(TRANSACTION_TYPE_OPTIONS, listing?.transaction_type),
    real_estate_category: findSelectItem(
      REAL_ESTATE_CATEGORY_OPTIONS,
      listing?.real_estate_category,
    ),

    // Legal status
    legal_status: findSelectItem(LEGAL_STATUS_OPTIONS, listing?.legal_status),

    // Land plot info
    map_sheet_number: listing?.map_sheet_number || '',
    plot_number: listing?.plot_number || '',

    // Dimensions
    area: String(+listing?.area || +re?.area || ''),
    usage_area: String(+listing?.usage_area || ''),
    width: String(+re?.width || ''),
    length: String(+re?.length || ''),

    // Price
    price_type: findSelectItem(PRICE_TYPE_OPTIONS, listing?.price_type),
    price: String(+listing?.price || ''),
    price_deposit: String(+listing?.price_deposit || ''),

    // Direction fields
    direction: findSelectItem(DIRECTION_OPTIONS, listing?.direction),
    main_door_direction: findSelectItem(DIRECTION_OPTIONS, listing?.main_door_direction),
    balcony_direction: findSelectItem(DIRECTION_OPTIONS, listing?.balcony_direction),

    // Category-specific: property type
    property_type: findSelectItem(APARTMENT_TYPE_OPTIONS, listing?.property_type),

    // Category-specific: furniture status
    furniture_status: findSelectItem(FURNITURE_STATUS_OPTIONS, listing?.furniture_status),

    // Category-specific: handover status (apartment only)
    handover_status: findSelectItem(HANDOVER_STATUS_OPTIONS, listing?.handover_status),

    // Category-specific: land type (land only)
    land_type: findSelectItem(LAND_TYPE_OPTIONS, re?.land_type),

    // Category-specific: room counts
    rooms: listing?.rooms ?? null,
    bath_rooms: listing?.bath_rooms ?? null,
    floors: listing?.floors ?? null,

    // Apartment/Office: nested fields from real_estate
    block_number: re?.block_number || '',
    floor_number: re?.floor_number || '',

    // Apartment-specific fields
    unit_number: listing?.unit_number || '',

    // Land-specific fields
    expansion_size: listing?.expansion_size ?? null,
    residential_area: listing?.residential_area ?? null,

    // Building-specific fields
    estimated_cashflow: listing?.estimated_cashflow ?? null,
    elevator_count: listing?.elevator_count ?? null,

    // Attributes
    attributes: (listing?.attributes || []).map((attr: any) => ({
      id: attr.id,
      name: attr.name,
      value: attr.pivot?.value || attr.value || '',
      input_type: attr?.input_type || 'text',
    })),

    // Content
    title: listing?.title || '',
    description: nl2br(listing?.description || ''),

    // Relationships
    customer_id: listing?.customer_id || '',
    broker_id: listing?.broker_id || '',
    contract_id: listing?.contract?.id || '',

    // Location
    real_estate_id: re?.id || '',
    address: listing?.address || '',

    // Media
    video: listing?.video || {},
    photos: listing?.photos || [],
    panorama: listing?.panorama || {},
  }
}

export const formatDataToSubmit = (values: any) => {
  return {
    update_type: values.update_type || 'update',
    processing_status: values.processing_status?.code,
    real_estate_id: values.real_estate_id,
    customer_id: values?.customer_id || '',
    broker_id: values.broker_id || '',
    contract_id: values.contract_id || '',

    // Content
    title: values.title,
    description: values.description?.replace(/\n/g, '') || '',

    // Poster
    poster_type: values.poster_type,

    // Transaction
    transaction_type: values.transaction_type?.code,
    listing_type: values.listing_type?.code,
    real_estate_category: values.real_estate_category?.code,

    // Legal
    legal_status: values.legal_status?.code || '',

    // Location
    address: values.address,
    plot_number: values.plot_number,
    map_sheet_number: values.map_sheet_number,

    // Dimensions
    area: values.area,
    usage_area: values.usage_area,
    width: values.width || '',
    length: values.length || '',

    // Price
    price_type: values.price_type?.code || 'fixed_price',
    price: values.price,
    price_deposit: values.price_deposit || '',

    // Direction
    direction: values.direction?.code || '',
    main_door_direction: values.main_door_direction?.code || '',
    balcony_direction: values.balcony_direction?.code || '',

    // Category-specific
    property_type: values.property_type?.code || '',
    furniture_status: values.furniture_status?.code || '',
    handover_status: values.handover_status?.code || '',
    land_type: values.land_type?.code || '',
    rooms: values.rooms ?? null,
    bath_rooms: values.bath_rooms ?? null,
    floors: values.floors ?? null,
    block_number: values.block_number || '',
    floor_number: values.floor_number || '',

    // Apartment-specific
    unit_number: values.unit_number || '',

    // Land-specific
    expansion_size: values.expansion_size ?? null,
    residential_area: values.residential_area ?? null,

    // Building-specific
    estimated_cashflow: values.estimated_cashflow ?? null,
    elevator_count: values.elevator_count ?? null,

    // Media
    video: values.video?.uuid || '',
    photos: values.photos?.map((e: any) => e.uuid),
    panorama: values.panorama?.uuid || '',

    // Attributes — filter out items missing name (API requires name to be non-empty)
    attributes: (values.attributes || [])
      .filter((e: any) => e.name && String(e.name).trim() !== '')
      .map((e: any) => ({
        name: e.name,
        value: String(e.value || ''),
      })),
  }
}
